// src/components/profile-detail/utils/profileHelpers.js
import { alpha } from "@mui/material/styles";
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';

export const getGenderInfo = (profile, theme) => {
  if (!profile) {
    return {
      icon: null,
      text: 'Not specified',
      color: theme.palette.text.secondary,
      bgColor: theme.palette.action.hover,
      headerBg: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
    };
  }
  
  const gender = profile?.personal_details?.gender?.toLowerCase();
  if (gender === 'male' || gender === 'm') {
    return {
      icon: <MaleIcon />,
      text: 'Male',
      color: theme.palette.primary.main,
      bgColor: alpha(theme.palette.primary.main, 0.1),
      headerBg: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
    };
  } else if (gender === 'female' || gender === 'f') {
    return {
      icon: <FemaleIcon />,
      text: 'Female',
      color: theme.palette.secondary.main,
      bgColor: alpha(theme.palette.secondary.main, 0.1),
      headerBg: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`
    };
  }
  return {
    icon: null,
    text: 'Not specified',
    color: theme.palette.text.secondary,
    bgColor: theme.palette.action.hover,
    headerBg: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
  };
};

export const calculateVerificationScore = (profile) => {
  if (!profile?.documents_verification) return 0;
  
  const verifications = profile.documents_verification;
  const verificationTypes = ['cnic_verified', 'education_verified', 'employment_verified'];
  
  const total = verificationTypes.length;
  const trueCount = verificationTypes.filter(type => verifications[type] === true).length;
  
  return total > 0 ? Math.round((trueCount / total) * 100) : 0;
};

export const formatIncome = (income) => {
  if (!income && income !== 0) return '-';
  return `PKR ${income.toLocaleString('en-PK')}/month`;
};

export const formatDate = (dateString) => {
  if (!dateString) return 'Not specified';
  
  try {
    // Handle DD-MM-YYYY format
    const parts = dateString.split('-');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    
    // Handle other date formats
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    return dateString;
  }
};

export const getAge = (dob) => {
  if (!dob) return null;
  
  try {
    const parts = dob.split('-');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      const birthDate = new Date(year, month - 1, day);
      const today = new Date();
      
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      return age;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const getZodiacInfo = (dob, theme) => {
  if (!dob) return { sign: 'Unknown', color: theme.palette.text.secondary };
  
  const month = parseInt(dob.split('-')[1], 10);
  const day = parseInt(dob.split('-')[0], 10);
  
  // Zodiac signs with theme-based colors
  const zodiacSigns = [
    { sign: 'Aries', start: { month: 3, day: 21 }, end: { month: 4, day: 19 }, color: theme.palette.error.main },
    { sign: 'Taurus', start: { month: 4, day: 20 }, end: { month: 5, day: 20 }, color: theme.palette.success.main },
    { sign: 'Gemini', start: { month: 5, day: 21 }, end: { month: 6, day: 20 }, color: theme.palette.warning.main },
    { sign: 'Cancer', start: { month: 6, day: 21 }, end: { month: 7, day: 22 }, color: theme.palette.primary.main },
    { sign: 'Leo', start: { month: 7, day: 23 }, end: { month: 8, day: 22 }, color: theme.palette.secondary.main },
    { sign: 'Virgo', start: { month: 8, day: 23 }, end: { month: 9, day: 22 }, color: theme.palette.info.main },
    { sign: 'Libra', start: { month: 9, day: 23 }, end: { month: 10, day: 22 }, color: theme.palette.primary.light },
    { sign: 'Scorpio', start: { month: 10, day: 23 }, end: { month: 11, day: 21 }, color: theme.palette.error.light },
    { sign: 'Sagittarius', start: { month: 11, day: 22 }, end: { month: 12, day: 21 }, color: theme.palette.success.light },
    { sign: 'Capricorn', start: { month: 12, day: 22 }, end: { month: 1, day: 19 }, color: theme.palette.warning.light },
    { sign: 'Aquarius', start: { month: 1, day: 20 }, end: { month: 2, day: 18 }, color: theme.palette.info.light },
    { sign: 'Pisces', start: { month: 2, day: 19 }, end: { month: 3, day: 20 }, color: theme.palette.secondary.light }
  ];
  
  const sign = zodiacSigns.find(z => {
    if (z.start.month <= month && z.end.month >= month) {
      if (month === z.start.month && day >= z.start.day) return true;
      if (month === z.end.month && day <= z.end.day) return true;
      if (month > z.start.month && month < z.end.month) return true;
    }
    // Handle Capricorn crossing year boundary
    if (z.sign === 'Capricorn' && ((month === 12 && day >= 22) || (month === 1 && day <= 19))) {
      return true;
    }
    return false;
  });
  
  return sign || { sign: 'Unknown', color: theme.palette.text.secondary };
};